import { ActivityType } from '@/app/shared-components/Types/Activity';
import { IconName } from '@/app/shared-components/Types/Icons';
import { Language } from '@/app/shared-components/Types/Utils';
import { languageShortnames } from './helpers';
import { Paged } from '@/app/shared-components/Types/Paged';
import { SubscriptionStatus } from 'app/shared-components/Types/Subscription';
import { ServiceItem } from '@/app/providers/GlobalDataProvider';
import { formatDate, formatDateOnly } from './dateFormatters';

export const mapNumberToEnumValue = <T extends { [key: string]: string | number }>(
  value: number,
  enumType: T,
): keyof T | null => {
  const keys = Object.keys(enumType).filter(
    (key) => typeof enumType[key] === 'number' && enumType[key] === value,
  ) as (keyof T)[];

  if (keys.length > 0) {
    return keys[0];
  }
  return null;
};

export const formatServiceToEnum = (
  services: Pick<Paged<ServiceItem>, 'items'> | undefined,
): Record<string, any> => {
  return (
    services?.items.reduce<Record<string, any>>((acc, item) => {
      const key = item.id;
      const value = item.name.replace(/[^a-zA-Z0-9]/g, ' ');
      acc[key] = value;
      acc[value] = key;
      return acc;
    }, {}) ?? {}
  );
};

export const mapLanguageToShortname = (languageId: number | undefined) => {
  const language = languageId ?? Language.English;
  const languageName = mapNumberToEnumValue(language, Language);
  return languageName ? languageShortnames[languageName] : '';
};

export const mapArrayToSelectFieldData = (data: string[]) => {
  const items = data.map((item, index) => ({
    id: index + 1,
    name: item,
  }));
  return {
    items,
  };
};

// https://stackoverflow.com/a/68002993
export const convertEnumToArray = (enumType: object) => {
  const arrayObjects = [];
  // Retrieve key and values using Object.entries() method.
  for (const [propertyKey, propertyValue] of Object.entries(enumType)) {
    // Ignore keys that are not numbers
    if (!Number.isNaN(Number(propertyKey))) {
      continue;
    }

    // Add keys and values to array
    arrayObjects.push({ id: propertyValue, name: propertyKey });
  }

  return { items: arrayObjects };
};

export const activityIconMap: { [key in ActivityType]: IconName } = {
  [ActivityType['Creation Event']]: 'add' as IconName,
  [ActivityType['Incoming Email']]: 'email' as IconName,
  [ActivityType['Outgoing Email']]: 'forwardToInboxOutlined' as IconName,
  [ActivityType['Incoming Message']]: 'comment' as IconName,
  [ActivityType['Outgoing Message']]: 'chatOutlined' as IconName,
  [ActivityType['Incoming Phone Call']]: 'phoneCallbackOutlined' as IconName,
  [ActivityType['Outgoing Phone Call']]: 'phoneCall' as IconName,
  [ActivityType.Note]: 'note',
  [ActivityType['Status Change']]: 'edit' as IconName,
  [ActivityType['Stage Change']]: 'edit' as IconName,
  [ActivityType['Schedule Call']]: 'calendar' as IconName,
  [ActivityType['Edit']]: 'activityEdit' as IconName,
  [ActivityType['Dsi']]: 'edit' as IconName,
};

export const setColorClassBasedOnStatus = (status: SubscriptionStatus) => {
  switch (status) {
    case SubscriptionStatus.Pending:
      return '#ecc94b';
    case SubscriptionStatus.Completed:
      return '#48bb78';
    case SubscriptionStatus.Cancelled:
      return '#f56565';
  }
};

export const formatPhoneNumber = (phoneNumber: string | undefined | null) => {
  if (!phoneNumber) return '';
  return `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(3, 6)} - ${phoneNumber.substring(
    6,
  )}`;
};

export const sanitizeMaskedInputValue = (value: string, valueLength = 1) =>
  value.replace(/\D/g, '').slice(0, valueLength);

function extracted(
  payload: any,
  imageFields: string[],
  formData: FormData,
  nestedKey: string | undefined = undefined,
) {
  Object.keys(payload).forEach((key) => {
    const value = payload[key as keyof typeof payload];

    if (imageFields.includes(key) && value instanceof FileList) {
      formData.append(nestedKey ? `${nestedKey}.${key}` : key, value[0]);
    } else if (Array.isArray(value) && value.length > 0) {
      value.forEach((val) => formData.append(key, val));
    } else if (typeof value === 'object' && value !== null) {
      extracted(value, [], formData, key);
    } else if (value != undefined) {
      formData.append(nestedKey ? `${nestedKey}.${key}` : key, value.toString());
    }

    return formData;
  });
}

export const transformPayloadToFormData = (payload: any, imageFields: string[]) => {
  const formData = new FormData();
  extracted(payload, imageFields, formData);
  return formData;
};

export const removeInlineStyles = (html: string) => {
  return html.replace(/style\s*=\s*"(?:\\.|[^"\\])*"/g, '');
};

export type InfoType =
  | 'phone'
  | 'email'
  | 'date'
  | 'address'
  | 'esiid'
  | 'dateOnly'
  | 'name'
  | 'text';

export const valueFormatter = (type: InfoType, value?: string | Date | null): string => {
  if (type === 'phone' && typeof value === 'string') {
    return formatPhoneNumber(value);
  }
  if (type === 'date') {
    return formatDate(value);
  }
  if (type === 'dateOnly') {
    return formatDateOnly(value);
  }
  return value as string;
};

export const formatCurrency = (value: number | string | undefined | null) => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(numValue)) return '';

  return new Intl.NumberFormat(navigator.language, {
    style: 'currency',
    currency: 'USD',
  }).format(numValue ?? 0);
};

export const formatCurrencyWithoutFraction = (value: number) => {
  if (!Number.isFinite(value)) return '';

  return new Intl.NumberFormat(navigator.language, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatElectricityServiceCurrency = (value: number) => {
  if (!Number.isFinite(value)) return '';

  return new Intl.NumberFormat(navigator.language, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(value);
};
